import React, { useEffect, useState } from 'react';
import api from '../api';
import { DataGrid } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const columns = [
    { field: 'id', headerName: 'ID', flex: 0.5, minWidth: 70 },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
    {
        field: 'action',
        headerName: 'Actions',
        flex: 1,
        minWidth: 160,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="contained" onClick={() => params.row.onEdit(params.row)}>Edit</Button>
                <Button size="small" color="error" variant="outlined" onClick={() => params.row.onDelete(params.row)}>Delete</Button>
            </Box>
        ),
    },
];

const AthleticConf = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [name, setName] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => { load(); }, []);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/public/athletic-conferences');
            const data = res.data || [];
            setItems(data);
        } catch (e) {
            setSnackbar({ open: true, message: 'Failed to load list', severity: 'error' });
        } finally { setLoading(false); }
    };

    const handleAdd = () => { setEditing(null); setName(''); setModalOpen(true); };
    const handleEdit = (it) => { setEditing(it); setName(it.name || ''); setModalOpen(true); };

    const handleSave = async () => {
        try {
            if (editing) {
                await api.put(`/admin/api/athletic-conferences/${editing.id}`, { name });
            } else {
                await api.post('/admin/api/athletic-conferences', { name });
            }
            setModalOpen(false);
            load();
            setSnackbar({ open: true, message: 'Saved', severity: 'success' });
        } catch (e) {
            setSnackbar({ open: true, message: 'Save failed', severity: 'error' });
        }
    };

    const handleDelete = async (it) => {
        if (!window.confirm('Delete this conference?')) return;
        try {
            await api.delete(`/admin/api/athletic-conferences/${it.id}`);
            load();
            setSnackbar({ open: true, message: 'Deleted', severity: 'success' });
        } catch (e) {
            setSnackbar({ open: true, message: 'Delete failed', severity: 'error' });
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 160px)', width: '90%', margin: '40px auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <Typography variant="h4" component="h2">Athletic Conferences</Typography>
                <Button variant="contained" onClick={handleAdd}>Add</Button>
                <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
            </Box>
            <Box sx={{ flex: 1, width: '100%' }}>
                <DataGrid
                    rows={items.map(i => ({ ...i, onEdit: handleEdit, onDelete: handleDelete }))}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    loading={loading}
                    getRowId={(row) => row.id}
                    disableColumnMenu
                    sx={{
                        height: '100%',
                        '& .MuiDataGrid-columnHeaderTitle': { display: 'block', whiteSpace: 'normal', lineHeight: 1.2, fontWeight: 700, textDecoration: 'underline' },
                        '& .MuiDataGrid-columnHeader .MuiChip-root, & .MuiDataGrid-columnHeader .MuiChip-label': { fontWeight: 400, textDecoration: 'none' },
                    }}
                />
            </Box>

            <Dialog open={modalOpen} onClose={() => setModalOpen(false)}>
                <DialogTitle>{editing ? 'Edit' : 'Add'} Athletic Conference</DialogTitle>
                <DialogContent>
                    <TextField autoFocus fullWidth label="Name" value={name} onChange={(e) => setName(e.target.value)} sx={{ mt: 1 }} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModalOpen(false)}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
};

export default AthleticConf;
